import { SourceType, isDefined, schSourceType } from '@aloft/toolkit-shared';
import { Icon, Input, Tooltip } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Popover, PopoverContent, PopoverTrigger } from '@aloft/toolkit-sui/chakra';

import { useRef, useState } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';

import { getDataSourceDisplayInfo, getSelectSourceTooltip } from '../utils.ts';
import { EditableFieldTrigger } from './shared/EditableFieldTrigger.tsx';
import { PopoverInputHeader } from './shared/PopoverInputHeader.tsx';
import { PopoverOptionsList } from './shared/PopoverOptionsList.tsx';
import { useEditableField } from './shared/useEditableField.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import { popoverInputSx } from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

export const EditableField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
  required = false,
}: PropertyGridFieldProps) => {
  const { toDisplayValue, toEditValue, toValue } = field;

  const [currentParsedValue, setCurrentParsedValue] = useState<string | undefined>(undefined);
  const [currentSource, setCurrentSource] = useState<SourceType | undefined>(undefined);

  const {
    propertyId,
    propertyDataFieldName,
    value,
    displayValue,
    conflictedFields,
    selectedSourceValue,
    showConflictButton,
    setShowConflictButton,
    inputFocused,
    setInputFocused,
    customInput,
    options,
    fieldHasConflict,
    makeHandleOnClose,
    resolveSource,
    commitSource,
  } = useEditableField({ columnIndex, field, isArv, control, getValues, setValue, required });

  const fieldRef = useRef<HTMLButtonElement>(null);
  const { isAlertTarget, clearAlertTarget } = usePropertyGridCellFocus({
    cellKeyId: field.fieldName,
    propertyId,
    fieldRef,
  });

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string,
    newSource?: SourceType,
  ) => {
    if (newSource === schSourceType.enum.unknown) {
      if (required) return;
      onChange(undefined);
      commitSource(schSourceType.enum.unknown);
      setCurrentSource(schSourceType.enum.unknown);
      return;
    }
    const parsedNewValue = toValue ? toValue(newValue, propertyId) : newValue;
    setCurrentParsedValue(parsedNewValue);
    onChange(parsedNewValue);

    // We need to re-call toDisplayValue here to ensure we get the correct display value.
    // This is necessary for some field types like AloftArea where the field accepts a string output
    // but the toDisplayValue() function's result is a formatted area string.
    const displayParsedValue = toDisplayValue
      ? toDisplayValue(parsedNewValue, propertyId)
      : parsedNewValue;

    const resolvedSource = resolveSource(parsedNewValue, newSource, displayParsedValue);
    commitSource(resolvedSource);
    setCurrentSource(resolvedSource);
    clearAlertTarget();
  };

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        let editValue = toEditValue ? toEditValue(value, propertyId) : displayValue;
        const currentEditValue = toEditValue
          ? toEditValue(currentParsedValue, propertyId)
          : currentParsedValue;

        if (currentParsedValue !== undefined && currentEditValue !== editValue) {
          editValue = currentEditValue as string;
        }

        let selectedSource = selectedSourceValue ?? (isDefined(value) ? 'custom' : 'none');

        if (currentSource !== undefined && currentSource !== selectedSource) {
          selectedSource = currentSource;
        }

        const showIcon = isDefined(error) || selectedSource !== 'none';
        const icon = error
          ? 'error-warning'
          : getDataSourceDisplayInfo(selectedSource ?? 'none').icon;
        const iconColor = error ? 'alertError' : undefined;
        const iconTooltipLabel = error ? error.message : getSelectSourceTooltip(selectedSource);

        return (
          <Popover gutter={-32} initialFocusRef={customInput} variant="inline" isLazy matchWidth>
            {({ onClose }) => {
              const handleOnClose = makeHandleOnClose(onClose);

              return (
                <>
                  <PopoverTrigger>
                    <EditableFieldTrigger
                      ref={fieldRef}
                      colSpan={colSpan}
                      displayValue={displayValue}
                      fieldHasConflict={fieldHasConflict}
                      showConflictButton={showConflictButton}
                      onMouseEnter={() => setShowConflictButton(true)}
                      onMouseLeave={() => setShowConflictButton(false)}
                      propertyDataFieldName={propertyDataFieldName}
                      conflictedFields={conflictedFields}
                      showIcon={showIcon}
                      icon={icon}
                      iconTooltipLabel={iconTooltipLabel ?? ''}
                      required={required}
                      isAlertTarget={isAlertTarget}
                    />
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <PopoverInputHeader inputFocused={inputFocused}>
                      <Input
                        type="text"
                        ref={(instance) => {
                          ref(instance);
                          customInput.current = instance;
                        }}
                        value={editValue}
                        __dangerDoNotUseSx={popoverInputSx}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => {
                          handleSetFieldValue(onChange, editValue);
                          setInputFocused(false);
                          handleOnClose();
                        }}
                        onChange={(v) => handleSetFieldValue(onChange, v)}
                        onClear={() => {
                          handleSetFieldValue(onChange, undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setInputFocused(false);
                            onClose();
                          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                            handleSetFieldValue(onChange, editValue);
                            setInputFocused(false);
                            onClose();
                          }
                        }}
                      />
                      <Tooltip label={iconTooltipLabel}>
                        <Icon icon={icon} color={iconColor} />
                      </Tooltip>
                    </PopoverInputHeader>
                    <PopoverOptionsList
                      options={options}
                      onSelect={({ value: newValue, source: newSource }) => {
                        handleSetFieldValue(onChange, newValue, newSource);
                        handleOnClose();
                      }}
                    />
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
